import React, { useEffect, useState } from "react";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { Btn, Field, Modal, inputCls } from "./kit";
import { VendorCards } from "./VendorCards";

const pinOnly = (v) => v.replace(/\D/g, "").slice(0, 6);

const ResidentPicker = ({ value, onChange }) => {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { apiClient.get("/cv/admin/residents").then(({ data }) => setAll(data.residents)).catch(() => {}); }, []);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const shown = all.filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <div className="relative mb-1"><Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
        <input data-testid="cv-residents-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents…" className={`${inputCls} pl-7`} /></div>
      <div className="max-h-44 overflow-y-auto border border-white/10 rounded-sm p-1" data-testid="cv-residents">
        {shown.map((r) => (
          <label key={r.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-white/5 cursor-pointer">
            <input type="checkbox" checked={value.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`cv-resident-${r.id}`} />{r.name}<span className="text-white/30">{r.status}</span></label>))}
      </div>
      <div className="text-[11px] text-white/40 mt-1" data-testid="cv-residents-count">{value.length} assigned · the vendor only ever sees these residents</div>
    </div>
  );
};

/** Invite (new) or edit a Contract Vendor. Invite sets the PIN through the shared Set login & PIN function and texts sign-in steps. */
export const VendorDialog = ({ vendor, onClose, onSaved }) => {
  const isNew = !vendor;
  const { user } = useAuth();
  const isSa = user?.role === "super_admin";
  const [cards, setCards] = useState(() => ({ portal: vendor?.portal_cards || ["workers", "docs"], access: null, orig: null }));
  const [f, setF] = useState(() => ({ name: vendor?.name || "", company: vendor?.company || "", phone: "", email: vendor?.email || "", pin: "", pin2: "",
    status: vendor?.status || "active", resident_ids: (vendor?.residents || []).map((r) => r.id) }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.name.trim() && f.company.trim() && (!isNew || (f.phone.trim() && f.pin.length === 6 && f.pin === f.pin2));
  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await apiClient.post("/cv/admin/vendors", { name: f.name, company: f.company, phone: f.phone, email: f.email, pin: f.pin, pin_confirm: f.pin2, resident_ids: f.resident_ids, portal_url: window.location.origin });
        toast.success(data.sms_status === "sent" ? "Vendor added · sign-in steps texted" : `Vendor added · text not sent (${String(data.sms_status).replace(/_/g, " ")})`);
      } else {
        await apiClient.patch(`/cv/admin/vendors/${vendor.id}`, { name: f.name, company: f.company, email: f.email, status: f.status, resident_ids: f.resident_ids,
          ...(isSa ? { portal_cards: cards.portal } : {}) });
        if (isSa && cards.access && JSON.stringify(Object.entries(cards.access).sort()) !== JSON.stringify(Object.entries(cards.orig || {}).sort())) {
          await apiClient.put(`/access/person/vendor/${vendor.id}`, { card_access: cards.access });
        }
        toast.success("Vendor saved");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Invite a Contract Vendor" : `Edit · ${vendor.name}`} testId="cv-dialog">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name"><input data-testid="cv-name" className={inputCls} value={f.name} onChange={set("name")} /></Field>
          <Field label="Company name"><input data-testid="cv-company" className={inputCls} value={f.company} onChange={set("company")} /></Field>
          {isNew && <Field label="Mobile number (their login)"><input data-testid="cv-phone" type="tel" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="(843) 555-0123" /></Field>}
          <Field label="Email (optional)"><input data-testid="cv-email" type="email" className={inputCls} value={f.email} onChange={set("email")} /></Field>
          {isNew && <Field label="PIN (6 digits)"><input data-testid="cv-pin" type="password" inputMode="numeric" autoComplete="new-password" className={inputCls} value={f.pin} onChange={(e) => setF({ ...f, pin: pinOnly(e.target.value) })} /></Field>}
          {isNew && <Field label="Confirm PIN"><input data-testid="cv-pin-confirm" type="password" inputMode="numeric" autoComplete="new-password" className={inputCls} value={f.pin2} onChange={(e) => setF({ ...f, pin2: pinOnly(e.target.value) })} /></Field>}
          {!isNew && <Field label="Account status"><select data-testid="cv-status" className={inputCls} value={f.status} onChange={set("status")}><option value="active">active</option><option value="inactive">inactive</option></select></Field>}
        </div>
        {isNew && f.pin2.length === 6 && f.pin !== f.pin2 && <p className="text-xs text-red-300" data-testid="cv-pin-mismatch">The two PINs don't match.</p>}
        <Field label="Assigned residents (their workers)"><ResidentPicker value={f.resident_ids} onChange={(v) => setF({ ...f, resident_ids: v })} /></Field>
        {!isNew && isSa && <Field label="Cards this vendor can use (Super Admin)"><VendorCards vendorId={vendor.id} value={cards} onChange={setCards} /></Field>}
        {isNew && <p className="text-[11px] text-white/50">We text them the portal link, their mobile number and this PIN. The PIN is never shown again or kept in plain text.</p>}
        <div className="flex justify-end gap-2">
          <Btn tone="muted" data-testid="cv-cancel" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="cv-save" disabled={!ok || busy} onClick={save}><Save size={12} /> {isNew ? "Invite & text" : "Save"}</Btn>
        </div>
      </div>
    </Modal>
  );
};
